import { TypedDocumentNode, gql } from '@apollo/client';
import { faChevronLeft } from '@fortawesome/pro-solid-svg-icons';
import { ReactNode } from 'react';
import { useIntl } from 'react-intl';
import styled from 'styled-components';

import { IconButton } from 'atoms/buttons/IconButton';
import { Horizontal, Vertical } from 'atoms/layout/flex';
import { LabelM } from 'atoms/typography';
import { ellipsis } from 'atoms/typography/Ellipsis';
import { PageHeader } from 'components/navigation/PageHeader';
import { Avatar } from 'components/user/Avatar';
import { ANY_SPORT_PLAY } from 'constants/__generated__/routes';
import { useSafePreviousNavigate } from 'hooks/useSafePreviousNavigate';
import { glossary } from 'lib/glossary';
import { tabletAndAbove } from 'style/mediaQuery';

import { MyClubPageHeader_user } from './__generated__/index.graphql';

const Wrapper = styled(Horizontal)`
  width: fit-content;
`;

const Nickname = styled.p`
  ${ellipsis}
  font: var(--t-bold) var(--t-label-m);
  @media ${tabletAndAbove} {
    font: var(--t-bold) var(--t-label-l);
  }
`;

type Props = {
  subtitle?: ReactNode;
  user: MyClubPageHeader_user;
};

export const MyClubPageHeader = ({ user, subtitle }: Props) => {
  const { formatMessage } = useIntl();
  const safePreviousNavigate = useSafePreviousNavigate(ANY_SPORT_PLAY);
  if (!user) {
    return null;
  }

  return (
    <PageHeader>
      <Horizontal gap={0.5}>
        <IconButton
          color="transparent"
          onClick={safePreviousNavigate}
          icon={faChevronLeft}
          aria-label={formatMessage(glossary.back)}
        />
        <Wrapper center>
          <Avatar user={user} size={4.5} rounded />
          <Vertical gap={0.5}>
            <Nickname>{user.nickname}</Nickname>
            {subtitle && <LabelM color="var(--c-nd-700)">{subtitle}</LabelM>}
          </Vertical>
        </Wrapper>
      </Horizontal>
    </PageHeader>
  );
};

MyClubPageHeader.fragments = {
  user: gql`
    fragment MyClubPageHeader_user on User {
      slug
      nickname
      ...Avatar_user
    }
    ${Avatar.fragments.user}
  ` as TypedDocumentNode<MyClubPageHeader_user>,
};
